import { useEffect, useState } from 'react';
import type {
  ApiClient, HeizlastLaufKopf, HeizlastJahresgang as Jahresgang,
} from '../api';
import { IconHeizung, IconPdf } from '../Icons';
import { GewerkUnterlagenZip } from '../UnterlagenZipKnopf';

const zahl = (n: number | null | undefined, z = 0) =>
  n === null || n === undefined ? '—' : n.toLocaleString('de-DE', {
    minimumFractionDigits: z, maximumFractionDigits: z });

const MONATE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
                'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

/**
 * Die Jahressimulation des Heizwaermebedarfs.
 *
 * Sie beantwortet eine ANDERE Frage als die Heizlast: nicht "wie gross muss
 * der Erzeuger sein", sondern "wie viel Waerme braucht das Gebaeude". Beide
 * haengen bei diesem Projekt kaum zusammen - die Bowl hat mit 800.000 m³/h
 * eine gewaltige Auslegungslast, wird aber nur an Veranstaltungstagen so
 * gefahren.
 *
 * Deshalb stehen die Vollbenutzungsstunden gross oben: Sie sind die Zahl, an
 * der man sieht, dass die Auslegungslast nicht der Verbrauch ist.
 */
export function Heizjahresgang({ api, lauf }: {
  api: ApiClient;
  lauf: HeizlastLaufKopf | null;
}) {
  const [jahr, setJahr] = useState<Jahresgang | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [pdf, setPdf] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let weg = false;
    if (!lauf) { setJahr(null); return; }

    (async () => {
      try {
        const j = await api.heizlastJahresgang(lauf.id);
        if (!weg) setJahr(j);
      } catch (err) {
        if (!weg) setFehler(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => { weg = true; };
  }, [api, lauf]);

  const rechnen = async () => {
    if (!lauf) return;
    setLaeuft(true);
    setFehler(null);
    try {
      await api.heizlastJahressimulation(lauf.id);
      setJahr(await api.heizlastJahresgang(lauf.id));
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setLaeuft(false);
    }
  };

  /**
   * Einen der beiden Nachweise holen.
   *
   * Der Dateiname kommt aus der Antwort (`dateinameAusAntwort`) und traegt
   * die Dokumentnummer vorn - genau den Namen, der im Berichtsregister
   * steht. Ein hier zusammengesetzter Name waere eine zweite Stelle, an der
   * derselbe Name entsteht.
   */
  const holePdf = async (was: 'jahr' | 'last') => {
    if (!lauf || pdf !== null) return;
    setPdf(was);
    setFehler(null);
    try {
      if (was === 'jahr') await api.heizjahresgangBericht(lauf.id);
      else await api.heizlastBericht(lauf.id, 'raeume');
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setPdf(null);
    }
  };

  if (!lauf)
    return (
      <div className="karte" style={{ marginTop: 18 }}>
        <div className="leer">
          Noch kein Heizlastlauf — die Simulation baut auf seinen
          Wärmeverlustkoeffizienten auf.
        </div>
      </div>
    );

  const k = jahr?.kennzahlen ?? null;

  return (
    <div className="karte" style={{ marginTop: 18 }}>
      <div className="bereich-kopf">
        <h2 className="karte-titel" style={{ margin: 0,
              display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <IconHeizung size={18} /> Jahressimulation Heizwärmebedarf
        </h2>
        <span className="marke neutral">Lauf {lauf.laufnummer}</span>
      </div>

      <details className="erlaeuterung">
        <summary>Erläuterung</summary>
        <div>
          Stündliche Bilanz über die DWD-Messreihe mit denselben
          Wärmeverlustkoeffizienten wie der Auslegungslauf:
          Bedarf = max(0, (H<sub>T</sub>+H<sub>V</sub>)·(θ<sub>int</sub>−θ<sub>e</sub>)
          − η·Gewinne). Die Gewinne kommen aus Personen, Beleuchtung, Geräten
          und der Sonne durch die Verglasung. Oberhalb der Heizgrenze wird
          nicht geheizt. Nicht enthalten ist die Speicherträgheit der Bauteile
          — über ein Jahr mittelt sich das weitgehend heraus, bei einer
          Spitzenlast nicht, und dafür ist die Heizlast zuständig.
        </div>
      </details>

      <button className="knopf" disabled={laeuft}
              onClick={() => void rechnen()}>
        {laeuft ? 'simuliert …' : jahr?.tage.length
          ? 'Simulation wiederholen' : 'Jahr simulieren'}
      </button>

      {/* --- Die Nachweise ------------------------------------------------
          Der Jahresgangknopf bleibt gesperrt, solange es keine Zeilen gibt:
          Der Endpunkt antwortete dann mit 409 „Es gibt nichts auszugeben",
          und ein Knopf, der zuverlaessig eine Fehlermeldung liefert, ist
          kein Knopf.

          Das ZIP-Paket „Lasten" steht hier UND im Kuehllast-Jahresgang -
          beide Lastfaelle liegen darin, und beide Seiten sind der Ort, an
          dem jemand danach greift. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                    flexWrap: 'wrap', marginTop: 12, paddingTop: 10,
                    borderTop: '1px solid var(--linie)' }}>
        <span className="sehr-schwach" style={{ marginRight: 4 }}>
          Als PDF:
        </span>

        <button className="knopf" disabled={pdf !== null || !jahr?.tage.length}
                onClick={() => void holePdf('jahr')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <IconPdf size={14} />
          {pdf === 'jahr' ? 'erzeugt …' : 'Jahresgang'}
        </button>

        <button className="knopf" disabled={pdf !== null}
                onClick={() => void holePdf('last')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <IconPdf size={14} />
          {pdf === 'last' ? 'erzeugt …' : 'Heizlastbericht'}
        </button>

        <GewerkUnterlagenZip
          api={api} projektId={lauf.projectId} gewerk="lasten"
          beschriftung="Lasten-Unterlagen als ZIP" />
      </div>

      {fehler && (
        <div className="hinweis fehler" style={{ marginTop: 10 }}>
          <span className="hinweis-zeichen">!</span><div>{fehler}</div>
        </div>
      )}

      {k && (
        <>
          <div className="kennzahlen" style={{ marginTop: 18 }}>
            <div>
              <div className="kennzahl-wert">{zahl(k.bedarfMwh, 0)}</div>
              <div className="kennzahl-name">MWh Heizwärmebedarf</div>
            </div>
            <div>
              <div className="kennzahl-wert">{zahl(k.gewinnMwh, 0)}</div>
              <div className="kennzahl-name">MWh Wärmegewinne</div>
            </div>
            <div>
              <div className="kennzahl-wert">{zahl(k.heizstunden, 0)}</div>
              <div className="kennzahl-name">
                Heizstunden von {zahl(k.stunden, 0)}
              </div>
            </div>
            <div>
              <div className="kennzahl-wert">
                {zahl(lauf.vollbenutzung, 0)}
              </div>
              <div className="kennzahl-name">Vollbenutzungsstunden</div>
            </div>
            <div>
              <div className="kennzahl-wert">{zahl(k.spitzeW / 1000, 0)}</div>
              <div className="kennzahl-name">
                kW Spitze am {k.spitzeTag}.{k.spitzeMonat}.
              </div>
            </div>
            <div>
              <div className="kennzahl-wert">{zahl(k.spitzeAussenC, 1)}</div>
              <div className="kennzahl-name">°C zur Jahresspitze</div>
            </div>
          </div>

          <p className="sehr-schwach" style={{ marginTop: 10 }}>
            Die Auslegungslast beträgt {zahl((lauf.normheizlastW ?? 0) / 1000, 0)} kW,
            die höchste simulierte Stunde {zahl(k.spitzeW / 1000, 0)} kW bei{' '}
            {zahl(k.spitzeAussenC, 1)} °C. Der Unterschied ist die
            Norm-Außentemperatur: Sie wird in einem gemessenen Jahr selten
            erreicht. Bei {zahl(lauf.vollbenutzung, 0)} Vollbenutzungsstunden
            läuft das Gebäude rechnerisch {zahl(lauf.vollbenutzung, 0)} Stunden
            mit voller Auslegungsleistung — die Jahresarbeit lässt sich aus der
            Auslegungslast nicht ableiten.
          </p>

          <h3 className="karte-titel" style={{ marginTop: 20 }}>
            Tagesbedarf über das Jahr
          </h3>
          <Jahreskurve tage={jahr!.tage} />

          <h3 className="karte-titel" style={{ marginTop: 20 }}>
            Monatsbilanz
          </h3>
          <table className="liste" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>Monat</th>
                <th style={{ textAlign: 'right' }}>Spitze</th>
                <th style={{ textAlign: 'right' }}>Heizwärme</th>
                <th style={{ textAlign: 'right' }}>Gewinne</th>
                <th style={{ textAlign: 'right' }}>Heizstunden</th>
              </tr>
            </thead>
            <tbody>
              {jahr!.monate.map((m) => (
                <tr key={m.monat}>
                  <td>{MONATE[m.monat - 1]}</td>
                  <td style={{ textAlign: 'right' }}>
                    {zahl(m.maxBedarfW / 1000, 0)} kW
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <strong>{zahl(m.arbeitKwh / 1000, 1)} MWh</strong>
                  </td>
                  <td style={{ textAlign: 'right' }} className="schwach">
                    {zahl(m.gewinnKwh / 1000, 1)} MWh
                  </td>
                  <td style={{ textAlign: 'right' }} className="schwach">
                    {zahl(m.heizstunden, 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

/** 365 Tagessummen als Säulen, mit der tiefsten Außentemperatur als Linie. */
function Jahreskurve({ tage }: { tage: Jahresgang['tage'] }) {
  if (tage.length === 0) return null;

  const breite = 900, hoehe = 220;
  const links = 58, rechts = 44, oben = 12, unten = 26;
  const zB = breite - links - rechts, zH = hoehe - oben - unten;

  const max = Math.max(1, ...tage.map((t) => t.arbeitKwh));

  const temps = tage.map((t) => t.minAussenC)
    .filter((t): t is number => t !== null);
  const tMax = temps.length > 0 ? Math.max(...temps) : 1;
  const tMin = temps.length > 0 ? Math.min(...temps) : 0;

  const y = (w: number) => oben + zH * (1 - w / max);
  const x = (i: number) => links + zB * (i / Math.max(1, tage.length - 1));
  const yT = (t: number) => oben + zH
    * (1 - (t - tMin) / (tMax - tMin === 0 ? 1 : tMax - tMin));

  const saeule = zB / Math.max(1, tage.length);

  const grenzen: { i: number; monat: number }[] = [];
  let letzter = 0;
  tage.forEach((t, i) => {
    if (t.monat !== letzter) { grenzen.push({ i, monat: t.monat }); letzter = t.monat; }
  });

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${breite} ${hoehe}`} width="100%"
           style={{ maxWidth: breite, display: 'block' }}
           role="img" aria-label="Jahresgang des Heizwärmebedarfs">
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={links} x2={breite - rechts} y1={y(max * f)} y2={y(max * f)}
                  stroke="var(--linie)" />
            <text x={links - 6} y={y(max * f) + 3} textAnchor="end"
                  fontSize={10} fill="var(--text-sehr-schwach)">
              {zahl(max * f / 1000, 1)} MWh
            </text>
          </g>
        ))}

        {tage.map((t, i) => (
          <rect key={i} x={x(i) - saeule / 2} y={y(t.arbeitKwh)}
                width={Math.max(0.6, saeule * 0.9)}
                height={Math.max(0, y(0) - y(t.arbeitKwh))}
                fill="#c25e5e" />
        ))}

        <line x1={links} x2={breite - rechts} y1={y(0)} y2={y(0)}
              stroke="var(--anthrazit)" />

        {temps.length > 0 && (
          <path fill="none" stroke="var(--akzent)" strokeWidth={1}
                strokeOpacity={0.7}
                d={tage.map((t, i) => t.minAussenC === null ? null
                    : `${i === 0 ? 'M' : 'L'}${x(i)},${yT(t.minAussenC)}`)
                  .filter(Boolean).join(' ')} />
        )}

        {grenzen.map((g) => (
          <text key={g.monat} x={x(g.i)} y={hoehe - 8} fontSize={10}
                textAnchor="start" fill="var(--text-sehr-schwach)">
            {MONATE[g.monat - 1]}
          </text>
        ))}
      </svg>

      <p className="sehr-schwach" style={{ fontSize: 12, marginTop: 6 }}>
        Rot: Heizwärmebedarf des Tages. Blau: tiefste Außentemperatur des
        Tages.
      </p>
    </div>
  );
}
